import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Stethoscope } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('Authentication failed - no session ID');
        navigate('/login', { replace: true });
        return;
      }

      try {
        // Exchange session_id for local session
        const response = await api.post('/auth/session', { session_id: sessionId });
        setUser(response.data);
        toast.success(`Welcome, ${response.data.name}!`);
        
        // Clear the hash and redirect to dashboard
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        const detail = error.response?.data?.detail;
        if (error.response?.status === 403) {
          toast.error(detail || 'No account found. Please contact an admin for an invitation.');
        } else {
          toast.error(detail || 'Authentication failed. Please try again.');
        }
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse">
          <Stethoscope className="w-9 h-9 text-white" />
        </div>
        <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
          Completing Sign In...
        </h2>
        <p className="text-slate-500">Please wait while we verify your account.</p>
        <div className="mt-6">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
